import { z } from 'zod';
import {
  ReceiptSchema,
  canTransitionReceipt,
  isReceiptEditable,
  type Receipt,
  type ReceiptStatus,
} from '../../domain/entities/receipt.ts';
import { CostLineSchema, type CostLine } from '../../domain/entities/cost-line.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * The cost inbox: writing receipts.
 *
 * Unlike a bill or a purchase order, a receipt is **created from the
 * client**. There is no number to allocate — a receipt is filed, not
 * issued, and nobody reads one out over the telephone — so a transaction
 * would buy nothing. Reviewing and rejecting are client writes for the same
 * reason: they are somebody's opinion with no side effect anywhere.
 *
 * **Converting one into a bill is a callable**, because that writes a bill
 * with an allocated number in another collection and marks the receipt in
 * the same breath. A client that could do half of it would leave a receipt
 * pointing at a bill that does not exist.
 */
export class ReceiptError extends Error {
  readonly reason: 'invalid' | 'notEditable' | 'badTransition';

  constructor(reason: 'invalid' | 'notEditable' | 'badTransition', message: string) {
    super(message);
    this.name = 'ReceiptError';
    this.reason = reason;
  }
}

export const SaveReceiptInputSchema = z.object({
  title: z.string().trim().min(1, 'A receipt needs a title').max(200),
  description: z.string().max(20_000).optional(),
  purchaseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  vendorContactId: z.string().min(1).nullable().optional(),
  lines: z.array(CostLineSchema).optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveReceiptInput = z.input<typeof SaveReceiptInputSchema>;

export interface ReceiptContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

type ReadContext = Pick<ReceiptContext, 'data' | 'companyId' | 'jobId'>;

function repo(context: ReadContext) {
  return context.data.collection<Receipt>(
    { companyId: context.companyId, jobId: context.jobId },
    'receipts',
  );
}

/** A job's receipts, newest first — the inbox is read from the top. */
export async function listReceipts(context: ReadContext): Promise<Receipt[]> {
  const page = await repo(context).list({ orderBy: [{ field: 'createdAt', direction: 'desc' }] });
  return [...page.items];
}

export function subscribeToReceipts(
  context: ReadContext,
  onChange: (receipts: readonly Receipt[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'createdAt', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function createReceipt(
  context: ReceiptContext,
  input: SaveReceiptInput,
): Promise<Receipt> {
  const parsed = SaveReceiptInputSchema.safeParse(input);
  if (!parsed.success) throw new ReceiptError('invalid', parsed.error.issues[0]!.message);

  const now = context.clock.now().toISOString();
  const checked = ReceiptSchema.safeParse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    description: parsed.data.description ?? '',
    ...(parsed.data.purchaseDate ? { purchaseDate: parsed.data.purchaseDate } : {}),
    ...(parsed.data.vendorContactId ? { vendorContactId: parsed.data.vendorContactId } : {}),
    status: 'new',
    lines: parsed.data.lines ?? [],
    tagIds: parsed.data.tagIds ?? [],
    source: 'upload',
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  if (!checked.success) throw new ReceiptError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(checked.data);
  return checked.data;
}

export async function saveReceipt(
  context: ReceiptContext,
  id: string,
  input: SaveReceiptInput,
): Promise<Receipt> {
  const parsed = SaveReceiptInputSchema.safeParse(input);
  if (!parsed.success) throw new ReceiptError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('receipts', id);
  if (!isReceiptEditable(existing.status)) {
    throw new ReceiptError(
      'notEditable',
      'This receipt is already a bill. Change the bill instead.',
    );
  }

  const optional = <K extends keyof Receipt>(key: K, value: string | null | undefined) =>
    value === null ? { [key]: undefined } : value !== undefined ? { [key]: value } : {};

  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    lines: parsed.data.lines ?? existing.lines,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    ...optional('purchaseDate', parsed.data.purchaseDate),
    ...optional('vendorContactId', parsed.data.vendorContactId),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = ReceiptSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new ReceiptError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'receipts' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Review or reject a receipt, or put a rejected one back.
 *
 * A client write, unlike every other status in this phase, because this one
 * has no side effect anywhere: it is somebody's opinion about a slip of
 * paper. `converted` is not reachable here and `canTransitionReceipt` says
 * so — that one writes a bill.
 */
export async function setReceiptStatus(
  context: ReceiptContext,
  id: string,
  to: ReceiptStatus,
): Promise<Receipt> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('receipts', id);
  if (existing.status === to) return existing;
  if (!canTransitionReceipt(existing.status, to)) {
    throw new ReceiptError(
      'badTransition',
      to === 'converted'
        ? 'Use New bill to convert a receipt, which writes the bill as well.'
        : `A ${existing.status} receipt cannot become ${to}.`,
    );
  }

  const patch = {
    status: to,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'receipts' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch };
}

export function withReceiptLineReplaced(
  receipt: Pick<Receipt, 'lines'>,
  line: CostLine,
): CostLine[] {
  const existing = receipt.lines.findIndex((candidate) => candidate.id === line.id);
  if (existing === -1) return [...receipt.lines, line];
  return receipt.lines.map((candidate) => (candidate.id === line.id ? line : candidate));
}

export function withReceiptLineRemoved(
  receipt: Pick<Receipt, 'lines'>,
  lineId: string,
): CostLine[] {
  return receipt.lines.filter((line) => line.id !== lineId);
}

/**
 * Turn a receipt into a bill.
 *
 * The `billId` is generated here unless supplied, which is what makes the
 * call safe to retry: the same id is the same bill, so a double-clicked
 * `New bill` converts once. The callable is idempotent on the receipt's own
 * state as well — a receipt already converted hands back the bill it made.
 */
export async function convertReceiptToBill(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly receiptId: string;
    readonly billId?: string;
  },
): Promise<{
  readonly billId: string;
  readonly number: string;
  readonly alreadyConverted: boolean;
}> {
  const billId = input.billId ?? newId();
  return functions.call('convertReceiptToBill', {
    companyId: input.companyId,
    jobId: input.jobId,
    receiptId: input.receiptId,
    billId,
  });
}
